import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Container,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ConfirmDialog from '../../components/confirm-dialog';
import CustomBreadcrumbs from '../../components/custom-breadcrumbs/CustomBreadcrumbs';
import { useSettingsContext } from '../../components/settings';
import Iconify from '../../components/iconify';
import { BrandConfig } from '../../config/brandConfig';
import { GuestGeneral } from '../../models/GuestGeneral';
import { ReservationGeneral } from '../../models/ReservationGeneral';
import { UserAccountGeneral } from '../../models/UserAccountGeneral';
import { usePatchRegistrationStepMutation } from '../../redux/rtkQuery/apiSlice';
import { PATH_APP } from '../../routes/paths';
import { checkAgeValid } from '../../utils/mrr/ageUtils';
import { cloud_getReservationGuests } from '../../utils/mrr/cloudFunctions';
import { StandardRTKQError } from '../error/StandardRTKQError';
import { AddGuestForm } from '../guest/GuestForm';
import HelpLink from './HelpLinks';
import { RegistrationAdditionalGuests } from './RegistrationAdditionalGuests';
import { RegistrationChangeHostForm } from './RegistrationChangeHostForm';
import { RegistrationFooter } from './RegistrationFooter';
import { RegistrationIdentityForm } from './RegistrationIdentityForm';
import { RegistrationMainGuestForm } from './RegistrationMainGuestForm';
import { RegistrationRentalAgreementForm } from './RegistrationRentalAgreementForm';
import RegistrationResortDisclosureForm from './RegistrationResortDisclosureForm';
import RegistrationServiceAnimalForm from './RegistrationServiceAnimalForm';
import { RegistrationStayReasonForm } from './RegistrationStayReasonForm';
import { RegistrationSummary } from './RegistrationSummary';
import { RegistrationVehiclesForm } from './RegistrationVehiclesForm';
import {
  isRentalAgreementResignPending,
  setRentalAgreementResignPending,
} from './docusignReturnStorage';
import {
  getChecklistOrder,
  RegistrationPicklistItem,
  RegistrationWizardStep,
  WizardStepInfo,
} from './types';

type RegistrationWizardPageShellProps = PropsWithChildren<{
  reservationName: string;
  titleText: string;
  subtitleText?: string | null;
  onCancel: VoidFunction;
  /** Moves keyboard focus to the step heading when this value changes. */
  focusKey?: string | number;
}>;

export function RegistrationWizardPageShell({
  reservationName,
  titleText,
  subtitleText = null,
  onCancel,
  focusKey,
  children,
}: RegistrationWizardPageShellProps) {
  const { themeStretch } = useSettingsContext();
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const isFirstFocusPass = useRef(true);

  useEffect(() => {
    if (focusKey === undefined) return;
    // Skip the initial mount so we do not steal focus from the page load / guard.
    if (isFirstFocusPass.current) {
      isFirstFocusPass.current = false;
      return;
    }
    stepHeadingRef.current?.focus();
  }, [focusKey]);

  return (
    <Container maxWidth={themeStretch ? 'md' : 'lg'} sx={{ pb: 5 }}>
      <CustomBreadcrumbs
        heading={reservationName}
        links={[
          { name: 'Home', href: PATH_APP.app },
          { name: 'Stays', href: PATH_APP.reservations },
          { name: 'Guest Registration' },
        ]}
        // action={
        //   <Button variant="text" onClick={onCancel}>
        //     Cancel
        //   </Button>
        // }
      />

      <Stack sx={{ mt: 1 }}>
        <Stack mb={1}>
          <Typography
            ref={stepHeadingRef}
            variant="h5"
            tabIndex={-1}
            sx={{ outline: 'none', '&:focus-visible': { outline: '2px solid', outlineOffset: 2 } }}
          >
            {titleText}
          </Typography>
          {subtitleText ? (
            <Typography variant="body1" color="text.secondary">
              {subtitleText}
            </Typography>
          ) : null}
        </Stack>
        {children}
      </Stack>
    </Container>
  );
}

interface RegistrationWizardContentProps {
  allowRegistration: boolean;
  ageRequired: boolean;
  reservation: ReservationGeneral;
  mainGuest: GuestGeneral | null;
  guestModels: GuestGeneral[] | null;
  sessionUser: UserAccountGeneral;
  registrationSteps: WizardStepInfo[];
  stepIndex: number;
  setStepIndex: (value: number) => void;
  loading: boolean;
  error: Error | null;
  callbackRetryGuests: VoidFunction;
  callbackMainGuestChange: VoidFunction;
  callbackRefreshReservation: (overrideBusy: boolean) => void;
  onCancel: VoidFunction;
  brand: BrandConfig;
}

export function RegistrationWizardContent({
  allowRegistration,
  ageRequired,
  reservation,
  mainGuest,
  guestModels,
  sessionUser,
  registrationSteps: registrationStepsProp,
  stepIndex,
  setStepIndex,
  loading,
  error,
  callbackRetryGuests,
  callbackMainGuestChange,
  callbackRefreshReservation,
  onCancel,
  brand,
}: RegistrationWizardContentProps) {
  const [ageRequirementsPassed, setAgeRequirementsPassed] = useState(true);
  const [activeGuest, setActiveGuest] = useState<GuestGeneral | null>(null);
  const [rentalAgreementConfirmOpen, setRentalAgreementConfirmOpen] = useState(false);
  const [startOverConfirmOpen, setStartOverConfirmOpen] = useState(false);
  const [pendingStepIndex, setPendingStepIndex] = useState<number | null>(null);
  const [resignRentalAgreement, setResignRentalAgreement] = useState(() =>
    isRentalAgreementResignPending(reservation.name)
  );
  const [
    sendPatchRegistrationStep,
    { isLoading: patchStepIsLoading, isSuccess: patchStepIsSuccess },
  ] = usePatchRegistrationStepMutation();

  //NOTE: A pending re-sign means Salesforce still has the agreement marked complete, but the copy
  //      the guest signed is stale. Treating the step as incomplete here is what routes them back
  //      through DocuSign, shows the checklist item unchecked, and keeps the presubmit below from
  //      declaring registration finished.
  const registrationSteps = useMemo(
    () =>
      resignRentalAgreement
        ? registrationStepsProp.map((s) =>
            s.key === RegistrationWizardStep.RentalAgreement ? { ...s, completed: false } : s
          )
        : registrationStepsProp,
    [registrationStepsProp, resignRentalAgreement]
  );

  const markRentalAgreementForResign = useCallback(() => {
    setRentalAgreementResignPending(reservation.name, true);
    setResignRentalAgreement(true);
  }, [reservation.name]);

  useEffect(() => {
    // Handle the case where the registration is required, steps have been completed, but the registration is not marked complete
    if (patchStepIsSuccess || patchStepIsLoading) {
      return; // EXIT
    }
    if (isRentalAgreementResignPending(reservation.name)) {
      return; // EXIT: the agreement is stale, so registration is not actually complete
    }
    const isRegistrationCompleted = reservation.require_guest_registration
      ? reservation.guest_registration_requirements.every((requirement) =>
          reservation.guest_registration_completed.includes(requirement)
        )
      : false;

    const shouldPresubmitRegistrationForm =
      reservation.require_guest_registration && isRegistrationCompleted;

    if (shouldPresubmitRegistrationForm) {
      // All guest registration requirements are complete.
      // Note: Completing the rental agreement form is what triggers reservation.guest_registration_complete to be set to true.
      const sfPayload = {
        reservationName: reservation.name,
        registrationStep: RegistrationPicklistItem.RENTAL_AGREEMENT,
      };

      sendPatchRegistrationStep(sfPayload);
    } else {
      // Not all guest registration requirements are complete. Should not submit form.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect at initial render.
  }, []);

  //NOTE: Once the Rental Agreement step is signed and completed, the user can no longer change the main host.
  //      However, additional guests can still be added, edited, or deleted after the RA step is completed.
  const rentalAgreementStepCompleted = useMemo(() => {
    const raStep = registrationSteps.find((s) => s.key === RegistrationWizardStep.RentalAgreement);
    if (!raStep) {
      console.warn('rental agreement step not found');
      return false;
    }
    return raStep.completed;
  }, [registrationSteps]);

  const currentEnum = registrationSteps[stepIndex].key;

  const requestStepTransition = useCallback(
    (targetStepIndex: number) => {
      const targetStep = registrationSteps[targetStepIndex];
      if (!targetStep) {
        return;
      }
      if (targetStep.key !== RegistrationWizardStep.RentalAgreement) {
        setStepIndex(targetStepIndex);
        return;
      }
      setPendingStepIndex(targetStepIndex);
      setRentalAgreementConfirmOpen(true);
    },
    [registrationSteps, setStepIndex]
  );

  /**
   * @param stepModified true when the step actually wrote a change, rather than the guest passing
   *                     back through an already-completed step without editing anything.
   */
  const handleContinue = (stepModified: boolean = false) => {
    const submittedStep = registrationSteps[stepIndex];

    //NOTE: Any write on an earlier step can stale a DocuSign PDF that already exists in Salesforce,
    //      even if this browser never stored the envelope id (cancel/return, new tab, etc.).
    if (
      (stepModified === true || submittedStep.subStep) &&
      submittedStep.key !== RegistrationWizardStep.Greeting &&
      submittedStep.key !== RegistrationWizardStep.RentalAgreement &&
      submittedStep.key !== RegistrationWizardStep.IdentityVerification
    ) {
      markRentalAgreementForResign();
    }

    if (registrationSteps[stepIndex].subStep) {
      // special logic for substeps, which jump around rather than using +/- 1
      switch (currentEnum) {
        case RegistrationWizardStep.SubChangeHost: {
          const mainGuestStepIndex = registrationSteps.findIndex(
            (s) => s.key === RegistrationWizardStep.MainGuest
          );
          if (mainGuestStepIndex === -1) {
            console.warn('MainGuest step not found');
            setStepIndex(0);
            return;
          }

          //NOTE: This assumes that MainGuest is _not_ the final step in the wizard.
          setStepIndex(mainGuestStepIndex + 1);
          return;
        }
        case RegistrationWizardStep.SubAddGuest:
        case RegistrationWizardStep.SubEditGuest: {
          const additionalGuestsStepIndex = registrationSteps.findIndex(
            (s) => s.key === RegistrationWizardStep.AdditionalGuests
          );
          if (additionalGuestsStepIndex === -1) {
            console.warn('AdditionalGuests step not found; from ' + currentEnum);
            setStepIndex(0);
            return;
          }

          // no advance, just back to all-guests
          setStepIndex(additionalGuestsStepIndex);
          return;
        }
        default:
          break;
      }
    }

    if (currentEnum === RegistrationWizardStep.Summary) {
      onCancel();
      return;
    }

    if (stepIndex + 1 >= registrationSteps.length) {
      //NOTE: This is now very unlikely, due to the substeps (which get sorted to the end).
      //      It's essentially a failsafe, which is why it calls onCancel().
      callbackRefreshReservation(false);
      onCancel();
      return;
    }

    if (registrationSteps[stepIndex + 1].subStep) {
      //NOTE: This assumes that _all_ substeps have been sorted to the end of registrationSteps!
      callbackRefreshReservation(false);

      // no call to onCancel(), we'll let the guard close us
      return;
    }

    // from the greeting page, we jump to first unfinished step, as a user convenience
    if (currentEnum === RegistrationWizardStep.Greeting) {
      //NOTE: Regarding rental-agreement already signed, here:
      //      Our rule for completing registration is all-steps-checked. However, an admin can interfere with
      //      those steps in SF. They can create gaps.
      //      Since you can't change the main host _after_ the rental agreement has been signed, this means a step
      //      can be permanently inaccessible. In that case, registration can't be completed from the portal.
      //      That case never gets here. We block the wizard with a 'Contact Guest Experience.' prompt.

      for (let i = stepIndex + 1; i < registrationSteps.length; i++) {
        if (!registrationSteps[i].completed) {
          requestStepTransition(i);
          return;
        }
      }
    }

    requestStepTransition(Math.min(registrationSteps.length - 1, stepIndex + 1));
  };

  const handleSubNavChangeHost = () => {
    if (rentalAgreementStepCompleted) {
      return;
    }

    //NOTE: 'Sub nav' is for certain step that don't nav with +/- 1. Instead, we jump to it, then jump back.
    //      It's marked as 'subStep', meaning it's a child of the another step, in this case Main Guest.
    if (currentEnum !== RegistrationWizardStep.MainGuest) {
      console.warn('Change Host only available on MainGuest step');
    }
    const changeHostStepIndex = registrationSteps.findIndex(
      (s) => s.key === RegistrationWizardStep.SubChangeHost
    );
    if (changeHostStepIndex === -1) {
      console.warn('ChangeHost step not found');
      setStepIndex(0);
      return;
    }
    setStepIndex(changeHostStepIndex);
  };

  const handleSubNavAddGuest = () => {
    if (currentEnum !== RegistrationWizardStep.AdditionalGuests) {
      console.warn('Add Guest only available on AdditionalGuests step');
    }
    const addGuestStepIndex = registrationSteps.findIndex(
      (s) => s.key === RegistrationWizardStep.SubAddGuest
    );
    if (addGuestStepIndex === -1) {
      console.warn('AddGuest step not found');
      setStepIndex(0);
      return;
    }
    setStepIndex(addGuestStepIndex);
  };

  const handleSubNavEditGuest = () => {
    if (currentEnum !== RegistrationWizardStep.AdditionalGuests) {
      console.warn('Edit Guest only available on AdditionalGuests step');
    }
    const editGuestStepIndex = registrationSteps.findIndex(
      (s) => s.key === RegistrationWizardStep.SubEditGuest
    );
    if (editGuestStepIndex === -1) {
      console.warn('EditGuest step not found');
      setStepIndex(0);
      return;
    }
    setStepIndex(editGuestStepIndex);
  };

  const handleNavBack = () => {
    // special logic on back form certain steps
    switch (currentEnum) {
      case RegistrationWizardStep.SubChangeHost: {
        const mainGuestStepIndex = registrationSteps.findIndex(
          (s) => s.key === RegistrationWizardStep.MainGuest
        );
        if (mainGuestStepIndex === -1) {
          console.warn('MainGuest step not found');
          setStepIndex(0);
          return;
        }
        setStepIndex(mainGuestStepIndex);
        return;
      }
      case RegistrationWizardStep.SubAddGuest:
      case RegistrationWizardStep.SubEditGuest: {
        const additionalGuestsStepIndex = registrationSteps.findIndex(
          (s) => s.key === RegistrationWizardStep.AdditionalGuests
        );
        if (additionalGuestsStepIndex === -1) {
          console.warn('additionalGuests step not found; form ' + currentEnum);
          setStepIndex(0);
          return;
        }
        setStepIndex(additionalGuestsStepIndex);
        return;
      }
      default:
        break;
    }

    setStepIndex(Math.max(0, stepIndex - 1));
  };

  // The greeting checklist, paired with each step's index in the wizard so a click can navigate.
  const checklistItems = useMemo(
    () =>
      registrationSteps
        .map((step, index) => ({ step, index }))
        .filter(
          ({ step }) =>
            !step.subStep &&
            step.key !== RegistrationWizardStep.Greeting &&
            step.key !== RegistrationWizardStep.Summary
        )
        .sort((a, b) => getChecklistOrder(a.step) - getChecklistOrder(b.step)),
    [registrationSteps]
  );

  const anyStepCompleted = checklistItems.some(({ step }) => step.completed);

  const handleStartOver = () => {
    setStartOverConfirmOpen(false);

    //NOTE: Salesforce has no way to un-complete a step from the portal, so the checkmarks stay put
    //      and we walk the guest through every step again. The stale agreement is what actually
    //      gets discarded: the next prepare voids the old envelope and issues a new one.
    markRentalAgreementForResign();

    // Restart at the first step the wizard runs, which is not necessarily first in the checklist.
    const firstStepIndex = registrationSteps.findIndex(
      (s) =>
        !s.subStep &&
        s.key !== RegistrationWizardStep.Greeting &&
        s.key !== RegistrationWizardStep.Summary
    );
    if (firstStepIndex >= 0) {
      requestStepTransition(firstStepIndex);
    }
  };

  // this enforces the all-guests-have-an-age rule
  useEffect(() => {
    if (ageRequired && currentEnum === RegistrationWizardStep.AdditionalGuests) {
      const allGuestsHaveAge = guestModels?.every(({ age }) => checkAgeValid(age));
      setAgeRequirementsPassed(allGuestsHaveAge === true);
      return;
    }
    if (!ageRequirementsPassed) {
      setAgeRequirementsPassed(true);
    }
  }, [ageRequired, ageRequirementsPassed, currentEnum, guestModels, stepIndex]);

  // Greeting has no previous step, so Back always exits to /reservations. Start Over sits next to
  // Continue when anything is already complete.
  let footerBackButton: ReactNode = <Button onClick={handleNavBack}>Back</Button>;
  if (currentEnum === RegistrationWizardStep.Greeting) {
    footerBackButton = <Button onClick={onCancel}>Back</Button>;
  } else if (currentEnum === RegistrationWizardStep.Summary) {
    footerBackButton = undefined;
  }

  const enableContinueButton =
    allowRegistration && ageRequirementsPassed && !loading && error === null;

  const overrideSMSOption =
    reservation.booking_channel == null ||
    (!reservation.booking_channel.includes('website') &&
      !reservation.booking_channel.includes('Airbnb') &&
      !reservation.booking_channel.includes('VRBO'));

  return (
    <>
      <RegistrationWizardPageShell
        reservationName={reservation.name}
        titleText={registrationSteps[stepIndex].title}
        subtitleText={registrationSteps[stepIndex].instruction}
        onCancel={onCancel}
        focusKey={`${stepIndex}-${registrationSteps[stepIndex]?.key ?? ''}`}
      >
        <Stack spacing={2}>
          {!allowRegistration
            ? !loading && (
                <Alert severity="warning">
                  You are not authorized to complete registration for this stay.
                </Alert>
              )
            : null}
          {error !== null && (
            <Stack spacing={1} alignItems="flex-start">
              <StandardRTKQError
                mutationCall={false}
                error={error}
                endpoint={cloud_getReservationGuests}
              />
              <Button variant="outlined" onClick={callbackRetryGuests}>
                Retry
              </Button>
            </Stack>
          )}
          {currentEnum === RegistrationWizardStep.Greeting && (
            <Stack spacing={2}>
              <Alert severity="warning">
                <AlertTitle>Registration Required</AlertTitle>
                <span style={{ fontSize: '1rem' }}>
                  Guest registration is required to confirm your reservation. Failure to register
                  will result in the cancelation of your reservation.
                </span>
              </Alert>
              <Typography fontWeight="bold">Please complete the following items:</Typography>
              <List
                disablePadding
                aria-label="Registration steps"
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}
              >
                {checklistItems.map(({ step, index }) => {
                  const completed = step.completed;
                  return (
                    <ListItem key={step.key} disablePadding sx={{ width: 'auto', display: 'block' }}>
                      <ListItemButton
                        aria-label={`${step.title}, ${completed ? 'completed' : 'not started'}`}
                        aria-disabled={!completed}
                        disableRipple={!completed}
                        onClick={() => {
                          if (!completed) return;
                          requestStepTransition(index);
                        }}
                        onKeyDown={(event) => {
                          // Keep incomplete items focusable but inert for activation keys.
                          if (!completed && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                          }
                        }}
                        sx={{
                          borderRadius: 1,
                          py: 0.5,
                          px: 1,
                          gap: 2,
                          width: 'auto',
                          // Scoped keyboard focus cue for checklist rows only (nav uses its own).
                          '&.Mui-focusVisible, &:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: 1,
                            backgroundColor: 'action.focus',
                          },
                          ...(!completed && {
                            cursor: 'default',
                            '&:hover': { backgroundColor: 'transparent' },
                          }),
                        }}
                      >
                        {completed ? (
                          <Iconify icon="mdi:checkbox-outline" width={22} aria-hidden />
                        ) : (
                          <Iconify icon="mdi:checkbox-blank-outline" width={22} aria-hidden />
                        )}
                        <Typography
                          variant="body1"
                          color={completed ? 'primary.main' : 'text.primary'}
                          sx={{ textDecoration: completed ? 'underline' : 'none' }}
                        >
                          {step.title}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Stack>
          )}
          {currentEnum === RegistrationWizardStep.MainGuest && (
            <>
              <List
                dense
                disablePadding
                sx={{
                  listStyleType: 'disc', // Display bullet points
                  pl: 2, // Padding left for the list
                  '& .MuiListItem-root': {
                    display: 'list-item', // Treat ListItem as list-item for bullets
                    paddingLeft: 0, // Remove default padding
                    marginLeft: 0, // Adjust margin to close gap
                  },
                }}
              >
                <ListItem>
                  <Typography>
                    The host is the person checking in and responsible for all other guests.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography>
                    If the host is a different person, please click change host.
                  </Typography>
                </ListItem>
              </List>
              <RegistrationMainGuestForm
                stepCompleted={registrationSteps[stepIndex].completed}
                ageRequired={ageRequired}
                minAge={ageRequired ? reservation.minimum_age : 0}
                mainGuest={mainGuest}
                sessionUser={sessionUser}
                sessionIsGuestOfRecord={
                  !!sessionUser.externalAccountID &&
                  sessionUser.externalAccountID === reservation.guest_id
                }
                reservationName={reservation.name}
                optOutSMS={overrideSMSOption}
                callbackCancel={() => {
                  handleNavBack();
                }}
                callbackChangeHost={() => {
                  handleSubNavChangeHost();
                }}
                callbackContinue={handleContinue}
              />
            </>
          )}
          {currentEnum === RegistrationWizardStep.SubChangeHost && (
            <RegistrationChangeHostForm
              ageRequired={ageRequired}
              minAge={ageRequired ? reservation.minimum_age : 0}
              mainGuest={mainGuest}
              reservationName={reservation.name}
              optOutSMS={overrideSMSOption}
              callbackMainGuestChange={callbackMainGuestChange}
              callbackCancel={() => {
                handleNavBack();
              }}
              callbackContinue={() => {
                handleContinue();
              }}
            />
          )}
          {currentEnum === RegistrationWizardStep.AdditionalGuests && (
            <RegistrationAdditionalGuests
              stepCompleted={registrationSteps[stepIndex].completed}
              mainGuest={mainGuest as GuestGeneral}
              reservation={reservation}
              guestModels={guestModels}
              callbackCancel={() => {
                handleNavBack();
              }}
              callbackAddGuest={() => {
                handleSubNavAddGuest();
              }}
              callbackEditGuest={(guest: GuestGeneral) => {
                setActiveGuest(guest);
                handleSubNavEditGuest();
              }}
              callbackContinue={handleContinue}
            />
          )}
          {currentEnum === RegistrationWizardStep.SubAddGuest && (
            <AddGuestForm
              showHelpLink
              gutterBottom={false}
              reservationName={reservation.name}
              optOutSMS={overrideSMSOption}
              guestToEdit={activeGuest}
              callbackCancel={() => {
                handleNavBack();
              }}
              callbackSubmit={() => {
                handleContinue();
              }}
              brand={brand}
            />
          )}
          {currentEnum === RegistrationWizardStep.SubEditGuest &&
            (activeGuest === null ? (
              <Alert severity="error">No guest selected to edit</Alert>
            ) : (
              <AddGuestForm
                showHelpLink
                gutterBottom={false}
                reservationName={reservation.name}
                optOutSMS={overrideSMSOption}
                guestToEdit={activeGuest}
                callbackCancel={() => {
                  handleNavBack();
                }}
                callbackSubmit={() => {
                  handleContinue();
                }}
                brand={brand}
              />
            ))}
          {currentEnum === RegistrationWizardStep.StayReason && (
            <RegistrationStayReasonForm
              stepCompleted={registrationSteps[stepIndex].completed}
              reservation={reservation}
              callbackCancel={() => {
                handleNavBack();
              }}
              callbackContinue={handleContinue}
            />
          )}
          {currentEnum === RegistrationWizardStep.Vehicles && (
            <>
              <List
                dense
                disablePadding
                sx={{
                  listStyleType: 'disc', // Display bullet points
                  pl: 2, // Padding left for the list
                  '& .MuiListItem-root': {
                    display: 'list-item', // Treat ListItem as list-item for bullets
                    paddingLeft: 0, // Remove default padding
                    marginLeft: 0, // Adjust margin to close gap
                  },
                }}
              >
                <ListItem>
                  <Typography>If you do not know at this time, please click Not Sure.</Typography>
                </ListItem>
                <ListItem>
                  <Typography>If you will not have a vehicle, please click No Vehicle.</Typography>
                </ListItem>
              </List>
              <RegistrationVehiclesForm
                stepCompleted={registrationSteps[stepIndex].completed}
                reservation={reservation}
                callbackCancel={() => {
                  handleNavBack();
                }}
                callbackContinue={handleContinue}
              />
            </>
          )}
          {currentEnum === RegistrationWizardStep.ServiceAnimal && (
            <RegistrationServiceAnimalForm
              stepCompleted={registrationSteps[stepIndex].completed}
              reservation={reservation}
              handleContinue={handleContinue}
              handleNavBack={handleNavBack}
            />
          )}
          {currentEnum === RegistrationWizardStep.ResortDisclosures && (
            <RegistrationResortDisclosureForm
              stepCompleted={registrationSteps[stepIndex].completed}
              reservation={reservation}
              callbackCancel={() => {
                handleNavBack();
              }}
              callbackContinue={handleContinue}
            />
          )}
          {currentEnum === RegistrationWizardStep.RentalAgreement && (
            <RegistrationRentalAgreementForm
              stepCompleted={registrationSteps[stepIndex].completed}
              stepFinal={registrationSteps[stepIndex].final}
              reservation={reservation}
              callbackContinue={() => {
                handleContinue();
              }}
              callbackCancel={() => {
                handleNavBack();
              }}
              callbackStartOver={() => {
                setStartOverConfirmOpen(true);
              }}
              enforceSessionIsMain
              mainGuest={mainGuest}
              sessionUser={sessionUser}
              guestsLoading={loading}
            />
          )}

          {currentEnum === RegistrationWizardStep.IdentityVerification && (
            <RegistrationIdentityForm
              stepCompleted={registrationSteps[stepIndex].completed}
              stepFinal={registrationSteps[stepIndex].final}
              reservation={reservation}
              callbackRefreshReservation={callbackRefreshReservation}
              callbackContinue={() => {
                handleContinue();
              }}
              enforceSessionIsMain
              mainGuest={mainGuest}
              sessionUser={sessionUser}
              guestsLoading={loading}
            />
          )}

          {currentEnum === RegistrationWizardStep.Summary && (
            <RegistrationSummary
              reservation={reservation}
              callbackClose={() => {
                onCancel();
              }}
            />
          )}
          {brand.helpLinks.guest_registration &&
            [
              RegistrationWizardStep.Greeting,
              RegistrationWizardStep.Summary,
              RegistrationWizardStep.TOTAL,
            ].includes(currentEnum) && (
              <HelpLink label="guest_registration" value={brand.helpLinks.guest_registration} />
            )}
          {/* Footer */}
          {!registrationSteps[stepIndex].rendersFooter && (
            <RegistrationFooter backButton={footerBackButton}>
              <Stack direction="row" spacing={2}>
                {currentEnum === RegistrationWizardStep.Greeting && anyStepCompleted ? (
                  <Button onClick={() => setStartOverConfirmOpen(true)}>Start Over</Button>
                ) : null}
                <LoadingButton
                  disabled={!enableContinueButton || patchStepIsLoading}
                  variant="contained"
                  loading={loading || patchStepIsLoading}
                  onClick={() => handleContinue()}
                >
                  {currentEnum !== RegistrationWizardStep.Summary ? 'Continue' : 'Close'}
                </LoadingButton>
              </Stack>
            </RegistrationFooter>
          )}
        </Stack>
      </RegistrationWizardPageShell>
      <ConfirmDialog
        open={rentalAgreementConfirmOpen}
        title="Continue to Docusign"
        content="Clicking 'Continue' will navigate you to Docusign for the rental agreement and identity verification."
        onClose={() => {
          setRentalAgreementConfirmOpen(false);
          setPendingStepIndex(null);
        }}
        sx={{
          position: 'fixed',
          inset: 0,
          '& .MuiDialog-container': {
            position: 'fixed',
            inset: 0,
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        action={
          <LoadingButton
            variant="contained"
            onClick={() => {
              if (pendingStepIndex !== null) {
                setStepIndex(pendingStepIndex);
              }
              setRentalAgreementConfirmOpen(false);
              setPendingStepIndex(null);
            }}
          >
            Continue
          </LoadingButton>
        }
      />
      <ConfirmDialog
        open={startOverConfirmOpen}
        title="Start over"
        content="You will be taken back through every registration step. If you have already signed the rental agreement, it will be replaced and you will need to sign again."
        onClose={() => {
          setStartOverConfirmOpen(false);
        }}
        sx={{
          position: 'fixed',
          inset: 0,
          '& .MuiDialog-container': {
            position: 'fixed',
            inset: 0,
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        action={
          <LoadingButton variant="contained" onClick={handleStartOver}>
            Start over
          </LoadingButton>
        }
      />
    </>
  );
}
